import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { getActionsForPlay, Play, useUpdateAdHocPlay } from "@/queries/plays";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AdHocPlayForm,
  AdHocPlayFormSubmission,
  AdHocPlayFormValues,
  adHocPlaySchema,
} from "./AdHocPlayForm";

interface UpdateAdHocPlayDialogProps {
  play: Play;
}

export function UpdateAdHocPlayDialog({ play }: UpdateAdHocPlayDialogProps) {
  const { mutate: updatePlayMutation, isPending: isUpdatingPlay } =
    useUpdateAdHocPlay(play.id);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  // Fetch actions for this play when dialog is open
  const { data: actionsData } = getActionsForPlay(play.id);

  const form = useForm<AdHocPlayFormValues>({
    resolver: zodResolver(adHocPlaySchema),
    defaultValues: {
      title: "",
      category: "General",
      location_type: "gate",
      location_details: "",
      terminal: null,
      concourse: null,
      start_interval: 0,
      end_interval: 0,
      actions: [{ title: "", team_id: null }],
    },
  });

  // Pre-fill form when the dialog opens or the play data changes
  useEffect(() => {
    if (isUpdateModalOpen && play) {
      // Prepare actions data - use fetched actions or default to one empty action
      const actionsForForm =
        actionsData && actionsData.length > 0
          ? actionsData.map((action, index) => ({
              title: action.message || "",
              team_id: action.team?.id || null,
              order: action.order !== undefined ? action.order : index,
            }))
          : [{ title: "", team_id: null, order: 0 }];

      form.reset({
        title: play.title || "",
        category: play.category || "General",
        location_type: play.location_type || "gate",
        location_details: play.location_details || "",
        terminal: play.terminal || null,
        concourse: play.concourse || null,
        start_interval: play.start_interval || 0,
        end_interval: play.end_interval || 0,
        target_date: play.target_date ? new Date(play.target_date) : new Date(),
        actions: actionsForForm,
      });
    } else if (!isUpdateModalOpen) {
      form.reset(); // Clear form when closing
    }
  }, [isUpdateModalOpen, play, actionsData, form.reset]);

  // Submit Handler
  const onPlaySubmit = (values: AdHocPlayFormSubmission) => {
    const filteredActions = values.actions
      .filter((action) => action.title.trim() !== "")
      .map((action, index) => ({
        title: action.title,
        team_id: action.team_id,
        order: action.order !== undefined ? action.order : index,
      }));

    const playData = {
      title: values.title,
      category: values.category,
      location_type: values.location_type,
      location_details: values.location_details || "",
      terminal: values.terminal || "",
      concourse: values.concourse || "",
      start_interval: values.start_interval,
      end_interval: values.end_interval,
      target_date: values.target_date,
      actions: filteredActions,
    };

    updatePlayMutation(playData, {
      onSuccess: () => {
        toast({
          title: "Success",
          description: "Play updated successfully.",
        });
        queryClient.invalidateQueries({ queryKey: ["plays"] });
        queryClient.invalidateQueries({ queryKey: ["single_play", play.id] });
        queryClient.invalidateQueries({ queryKey: ["actions", play.id] });
        setIsUpdateModalOpen(false);
      },
      onError: (err: Error) => {
        toast({
          variant: "destructive",
          title: "Error updating play",
          description: err.message,
        });
      },
    });
  };

  // Only show update dialog for adhoc plays that haven't been completed
  if (!play.adhoc || play.completed_at) {
    return null;
  }

  return (
    <Dialog open={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Pencil className="h-4 w-4" />
          <span className="sr-only">Edit Play</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1000px] bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100">
            Edit One-Time Play
          </DialogTitle>
          <DialogDescription className="text-slate-300">
            Modify the details for this one-time play.
          </DialogDescription>
        </DialogHeader>

        {/* Use the shared form component */}
        <div className="py-4">
          <AdHocPlayForm
            form={form}
            onSubmit={onPlaySubmit}
            isLoading={isUpdatingPlay}
            submitButtonText="Save Changes"
            onClose={() => setIsUpdateModalOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
